import { THEME_COLORS, DEFAULT_FONT } from '../config/theme.config.js'
import { VIEW_MODE } from '../config/auth.config.js'

/** Placeholder in a footer column — renders the group logo when set in v1 group settings. */
export const FOOTER_LOGO_TAG = '{{logo}}'

/**
 * Custom org-group branding applies only in the account (user) shell.
 * Group and admin shells always use default Vuokranet theme + logo.
 *
 * @param {object|null|undefined} user
 * @param {typeof VIEW_MODE[keyof typeof VIEW_MODE]|null|undefined} viewMode
 * @returns {boolean}
 */
function shouldUseCustomBranding(user, viewMode = VIEW_MODE.USER) {
  if (viewMode !== VIEW_MODE.USER) return false
  const group = user?.currentAccount?.group
  return Boolean(group && !user?.bypassCustomStyles && !group.bypassGroupStyles)
}

function applyUserStyles(user, viewMode) {
  const group = user?.currentAccount?.group
  const useCustom = shouldUseCustomBranding(user, viewMode)

  const colors = useCustom ? group.colors || THEME_COLORS : THEME_COLORS
  const font = useCustom ? group.fonts?.fontFamily || DEFAULT_FONT : DEFAULT_FONT

  if (!document.querySelector(`link[data-font="${font}"]`)) {
    const family = font.split(' ').join('+')
    const link = document.createElement('link')
    link.href = `https://fonts.googleapis.com/css2?family=${family}:wght@300;400;500;600;700&display=swap`
    link.rel = 'stylesheet'
    link.setAttribute('data-font', font)
    document.head.appendChild(link)
  }

  document.documentElement.style.setProperty('--font-family', `'${font}'`)
  for (const [key, value] of Object.entries(colors)) {
    document.documentElement.style.setProperty(`--${key}`, value)
  }
}

function resolveLogoUrl(user, viewMode) {
  const group = user?.currentAccount?.group
  const useCustom = shouldUseCustomBranding(user, viewMode)
  const logo = useCustom ? group.logo : null
  return logo ? `data:${logo.mimeType};base64,${logo.buffer}` : null
}

/**
 * Resolve the branded footer configured on the account's org group.
 * Mirrors v1 (`Default.vue` + `Navigation/Footer.vue`): account mode only,
 * custom styles enabled, `showFooter` set, three HTML columns with optional
 * `{{logo}}` placeholders.
 *
 * @param {object|null|undefined} user
 * @param {typeof VIEW_MODE[keyof typeof VIEW_MODE]|null|undefined} [viewMode]
 * @returns {{ visible: boolean, columns: [string, string, string], logoUrl: string|null }}
 */
function resolveFooter(user, viewMode = VIEW_MODE.USER) {
  const group = user?.currentAccount?.group
  const visible = Boolean(shouldUseCustomBranding(user, viewMode) && group?.showFooter)

  if (!visible) {
    return { visible: false, columns: ['', '', ''], logoUrl: null }
  }

  const texts = group.texts?.footer ?? {}
  return {
    visible: true,
    columns: [texts.column1 ?? '', texts.column2 ?? '', texts.column3 ?? ''],
    logoUrl: resolveLogoUrl(user, viewMode),
  }
}

class ThemeUtility {
  /**
   * Whether custom org-group branding should paint the document.
   *
   * @param {object|null|undefined} user
   * @param {typeof VIEW_MODE[keyof typeof VIEW_MODE]|null|undefined} [viewMode]
   * @returns {boolean}
   */
  usesCustomBranding(user, viewMode) {
    return shouldUseCustomBranding(user, viewMode)
  }

  /**
   * Apply a user's group styling to the document root — palette tokens,
   * `--font-family`, and the matching Google Font link (added once per font).
   * Falls back to the default palette and font outside the account shell or
   * when styling is bypassed via `user.bypassCustomStyles` /
   * `group.bypassGroupStyles`.
   *
   * @param {object|null|undefined} user
   * @param {typeof VIEW_MODE[keyof typeof VIEW_MODE]|null|undefined} [viewMode]
   * @returns {void}
   */
  styles(user, viewMode) {
    return applyUserStyles(user, viewMode)
  }

  /**
   * Resolve the active group logo as a base64 data URL. Returns `null` when
   * outside the account shell, or when the user has no group logo / styling
   * is bypassed.
   *
   * @param {object|null|undefined} user
   * @param {typeof VIEW_MODE[keyof typeof VIEW_MODE]|null|undefined} [viewMode]
   * @returns {string|null}
   */
  logo(user, viewMode) {
    return resolveLogoUrl(user, viewMode)
  }

  /**
   * Branded footer columns for the active account's org group.
   *
   * @param {object|null|undefined} user
   * @param {typeof VIEW_MODE[keyof typeof VIEW_MODE]|null|undefined} [viewMode]
   * @returns {{ visible: boolean, columns: [string, string, string], logoUrl: string|null }}
   */
  footer(user, viewMode) {
    return resolveFooter(user, viewMode)
  }
}

export default new ThemeUtility()
